import React, { useState } from "react"
import { categoryLabel, organizerLabel, playModeLabel, warningRuleLabel, warningText } from "./plannerLabels"
import { CANNOT_BE_SENT, REASON_LABELS, alternativesLine, canGenerateAnyway, daysYearLabel, draftClubLabel, editionNumber, handledLine, heldLine,
  lastHeldLabel, lastHeldSource, lineLabel, lineSourceText, reasonsLine, sourceLabel, unsaveable, weeksLabel } from "./generationLabels"

// A competition line as a toggle: pressed when the draft keeps it; its sizing rule and numbers on hover (ticket 11). A click toggles the tick
// chosen so far, so that clicks made before the server answers add up.
const LineToggle = ({ row, line, onChoose }) => <button type="button" aria-pressed={line.ticked} title={lineSourceText(line, editionNumber(row, line.source.eventId))}
  className={"ep-gen-line" + (line.ticked ? " is-on" : "")}
  onClick={() => onChoose(row.id, choice => ({ competitions: { [line.id]: !(choice.competitions?.[line.id] ?? line.ticked) } }))}>{lineLabel(line)}</button>

// One source of the review (ticket 17): the tick, the source (number, name, days), the draft's new dates with −1 / +1 week, its place, name
// and competitions (the suggestions collapsed), what already handles it, and its warnings. Name, club, organizer and capacities are edited
// in the editor after saving.
export default function GenerationRow({ row, settings, timeZone, onChoose }) {
  const [suggestions, setSuggestions] = useState(false)
  const { draft } = row
  const number = `#${row.source.eventNumber}`
  const handled = handledLine(row, timeZone)
  const reasons = reasonsLine(row)
  const alternatives = alternativesLine(row)
  const held = heldLine(row)
  const neverSaved = unsaveable(row)
  // A move adds up with the moves not answered yet.
  const move = weeks => onChoose(row.id, choice => ({ weeks: (choice.weeks ?? row.moveWeeks) + weeks }))
  return <div role="group" aria-label={`Draft from ${sourceLabel(row)}`} className={"ep-gen-row" + (row.ticked ? "" : " is-off")}>
    <span><input type="checkbox" className="ep-gen-tick" checked={row.ticked} disabled={neverSaved} aria-label={`Generate the draft from ${number}`}
      onChange={() => onChoose(row.id, choice => ({ ticked: !(choice.ticked ?? row.ticked) }))} /></span>
    <span className="ep-stack">
      <b>{sourceLabel(row)}</b>
      <span className="ep-meta">{daysYearLabel(row.source)}</span>
      {row.status === "cancelled" && !row.usesLastHeldEdition && <span className="ep-mark ep-mark-error">Cancelled</span>}
      {row.status === "cancelled" && row.lastHeldEdition && !row.usesLastHeldEdition && <button type="button" className="btn btn-link btn-sm p-0 text-left"
        onClick={() => onChoose(row.id, { lastHeldEdition: true })}>{`Use the last held edition: ${lastHeldLabel(row.lastHeldEdition)}`}</button>}
      {row.usesLastHeldEdition && <>
        <span className="ep-meta">{lastHeldSource(row)}</span>
        <button type="button" className="btn btn-link btn-sm p-0 text-left" onClick={() => onChoose(row.id, { lastHeldEdition: false })}>Use the cancelled edition</button>
      </>}
    </span>
    <span className="ep-stack">
      <b>{daysYearLabel(draft)}</b>
      <span className="ep-meta">{weeksLabel(draft.sources.dates)}</span>
      {neverSaved ? <span className="text-danger">{REASON_LABELS.datesNotInOrder}</span> : <span className="ep-gen-moves">
        <button type="button" className="btn btn-light btn-sm" aria-label="Move one week earlier" onClick={() => move(-1)}>−1 wk</button>
        <button type="button" className="btn btn-light btn-sm" aria-label="Move one week later" onClick={() => move(1)}>+1 wk</button>
      </span>}
    </span>
    <span className="ep-stack">
      <span>{draftClubLabel(draft.club)}</span>
      <span className="ep-meta">{[categoryLabel(settings, draft.category), playModeLabel(draft.playMode)].filter(Boolean).join(" · ")}</span>
      <span className="ep-meta">{organizerLabel(draft.organizer)}</span>
    </span>
    <span className="ep-stack">
      <span>{draft.name}</span>
      {draft.checkName && <span className="ep-mark ep-mark-soon" title="The name repeats a year or an edition number: check it in the editor after saving.">
        Check the name</span>}
      {handled && <span className="ep-meta">{handled}</span>}
      {alternatives && <span className="ep-meta">{alternatives}</span>}
      {held && <span className="ep-mark ep-mark-warn">{held}</span>}
      {reasons && <span className="ep-mark ep-mark-error">{reasons}</span>}
      {row.cannotBeSent && <span className="ep-mark ep-mark-warn">{CANNOT_BE_SENT[row.cannotBeSent] || "Cannot be sent"}</span>}
      {canGenerateAnyway(row) && <button type="button" className="btn btn-light btn-sm align-self-start" onClick={() => onChoose(row.id, { ticked: true })}>Generate anyway</button>}
    </span>
    <span className="ep-gen-lines">
      {draft.competitions.map(line => <LineToggle key={line.id} row={row} line={line} onChoose={onChoose} />)}
      {draft.suggested.length > 0 && <button type="button" className="btn btn-link btn-sm p-0" aria-expanded={suggestions}
        onClick={() => setSuggestions(!suggestions)}>{`+${draft.suggested.length} suggested`}</button>}
      {suggestions && draft.suggested.map(line => <LineToggle key={line.id} row={row} line={line} onChoose={onChoose} />)}
    </span>
    <span className="ep-gen-warnings">
      {row.warnings.map(warning => <span key={warning.key || warning.rule} className="ep-mark ep-mark-warn" title={warningText(warning)}>
        {warningRuleLabel(warning.rule)}</span>)}
    </span>
  </div>
}
